import React from 'react';
import { Container } from 'react-bootstrap';
import './ProcessSection.css';

const ProcessStep = ({ number, title, items, icon }) => (
  <div className="process-step">
    <div className="process-step-header">
      <span className="process-step-number">{number}</span>
      <span className="process-step-icon">{icon}</span>
    </div>
    <h3 className="process-step-title">{title}</h3>
    <div className="process-step-content">
      {items.map((item, index) => (
        item.type === 'heading' ? (
          <h4 key={index} className="process-step-content-heading">{item.text}</h4>
        ) : (
          <p key={index} className="process-step-content-item">{item.text}</p>
        )
      ))}
    </div>
  </div>
);

function ProcessSection() {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const processSteps = [
    {
      number: "01",
      title: "Vision Alignment Workshop",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Establish a shared understanding of GenAI\'s potential within the client\'s specific industry and organizational context' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Executive Briefing: Begin with a high-level presentation to key stakeholders on the latest advancements in GenAI and their relevance' },
        { type: 'item', text: 'Interactive Ideation Sessions: Facilitate discussions to identify strategic objectives, challenges, and opportunities where GenAI can drive value' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A unified vision statement and preliminary roadmap aligning GenAI initiatives with business goals' }
      ],
      icon: "🎯"
    },
    {
      number: "02",
      title: "Deep Dive Assessment",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Conduct a thorough analysis of the client\'s current state to identify gaps, readiness levels, and high-impact areas for GenAI integration' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Data Ecosystem Evaluation: Assess the quality, accessibility, and security of existing data assets' },
        { type: 'item', text: 'Technical Infrastructure Review: Examine current IT infrastructure for compatibility and scalability with GenAI solutions' },
        { type: 'item', text: 'Cultural Readiness Analysis: Gauge organizational openness to innovation and change' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A detailed assessment report highlighting opportunities, risks, and readiness factors' }
      ],
      icon: "📊"
    },
    {
      number: "03",
      title: "Customized Solution Design",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Develop a tailored GenAI strategy and solution architecture that addresses the client\'s unique needs' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Use Case Prioritization: Identify and rank potential GenAI applications based on impact and feasibility' },
        { type: 'item', text: 'Prototype Development: Create AI models or simulations to demonstrate potential outcomes' },
        { type: 'item', text: 'Collaborative Design Workshops: Work closely with client teams to refine solutions, ensuring alignment with operational realities' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A comprehensive solution blueprint, including technical specifications and implementation plans' }
      ],
      icon: "💡"
    },
    {
      number: "04",
      title: "Agile Implementation",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Deploy GenAI solutions using agile methodologies for rapid value realization while minimizing disruption' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Sprint Planning: Break down the implementation into manageable sprints with clear deliverables' },
        { type: 'item', text: 'Iterative Development: Continuously build, test, and refine GenAI models and systems' },
        { type: 'item', text: 'Stakeholder Reviews: Regularly present progress to stakeholders for feedback and alignment' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A functional GenAI solution deployed in a controlled environment, ready for scaling' }
      ],
      icon: "⚡"
    },
    {
      number: "05",
      title: "Integration and Scaling",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Seamlessly integrate the GenAI solution into existing systems and scale across the organization' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'System Integration: Ensure compatibility and interoperability with existing technologies' },
        { type: 'item', text: 'Process Re-engineering: Adapt business processes to leverage new capabilities fully' },
        { type: 'item', text: 'Scalability Planning: Develop strategies for expanding the solution across departments or units' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A fully integrated GenAI solution operational within the client\'s ecosystem' }
      ],
      icon: "🔄"
    },
    {
      number: "06",
      title: "Empowerment and Change Management",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Equip the client\'s workforce with the skills and mindset to embrace GenAI-enabled transformation' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Customized Training Programs: Develop and deliver training tailored to different user groups' },
        { type: 'item', text: 'Leadership Coaching: Provide executive coaching to lead and advocate for AI initiatives' },
        { type: 'item', text: 'Cultural Transformation Initiatives: Implement programs that foster innovation and adaptability' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'An empowered organization ready to maximize the benefits of GenAI' }
      ],
      icon: "👥"
    },
    {
      number: "07",
      title: "Continuous Optimization and Support",
      items: [
        { type: 'heading', text: 'Objective' },
        { type: 'item', text: 'Provide ongoing support to ensure sustained performance and adapt to evolving needs' },
        { type: 'heading', text: 'Process' },
        { type: 'item', text: 'Performance Monitoring: Use analytics to track outcomes and identify improvement areas' },
        { type: 'item', text: 'Regular Strategy Reviews: Reassess goals and adjust strategies in response to market changes' },
        { type: 'item', text: 'Innovation Updates: Keep the client informed about the latest GenAI advancements and how they can be leveraged' },
        { type: 'heading', text: 'Outcome' },
        { type: 'item', text: 'A long-term partnership focused on continuous improvement and innovation' }
      ],
      icon: "🔁"
    }
  ];

  return (
    <div className="process-section">
      <Container>
        <div className="process-header">
          <h2 className="process-title mb-4">Our Enterprise AI Framework</h2>
          <p className="process-description mb-12">
            Our comprehensive, seven-phase process guides enterprises through the journey of integrating Generative AI into their operations, ensuring each solution is tailored, impactful, and sustainable.
          </p>
        </div>
        
        <div className="process-timeline">
          {processSteps.slice(0, 6).map((step, index) => (
            <ProcessStep key={index} {...step} />
          ))}
        </div>

        <div className="process-timeline-final">
          <div className="final-step">
            <ProcessStep {...processSteps[6]} />
          </div>
          <div className="process-cta">
            <div className="process-cta-title">
              Ready to transform your business with AI?
            </div>
            <button 
              className="primary-button-white" 
              onClick={scrollToContact}
            >
              Get Started
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default ProcessSection;
